import { useEffect, useState } from 'react'
import { Box, IconButton } from '@mui/material'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import InventoryIcon from '@mui/icons-material/Inventory2'

/**
 * Parcours des visuels d'un article.
 *
 * ⚠️ Pas de défilement automatique. Une image qui bouge toute seule oblige à
 * attendre pour revoir celle qu'on regardait, et rend l'écran inutilisable
 * pour comparer deux photos — précisément ce qu'on fait devant un client.
 *
 * Les flèches n'apparaissent qu'à partir de deux visuels : une commande qui
 * ne commande rien apprend à l'utilisateur à ne plus la regarder.
 */
export function PhotoSlider({
  images,
  width = 220,
  height,
}: {
  images: string[]
  /** Nombre (pixels) ou valeur CSS — `min(80vw, 560px)` pour un affichage
   *  qui suit la taille de la fenêtre. */
  width?: number | string
  /** Omis : la hauteur découle du ratio 3:4, celui du recadrage à
   *  l'enregistrement. C'est ce qui permet une largeur relative. */
  height?: number | string
}): JSX.Element {
  const [index, setIndex] = useState(0)

  // Un visuel retiré pendant l'affichage laisserait l'index hors bornes.
  useEffect(() => {
    if (index > images.length - 1) setIndex(0)
  }, [images.length, index])

  const multiple = images.length > 1
  const courante = images[index]

  function aller(pas: number): void {
    setIndex((i) => (i + pas + images.length) % images.length)
  }

  return (
    <Box sx={{ width, flexShrink: 0 }}>
      <Box
        role={multiple ? 'group' : undefined}
        aria-label={multiple ? `Visuel ${index + 1} sur ${images.length}` : undefined}
        tabIndex={multiple ? 0 : -1}
        onKeyDown={(e) => {
          if (!multiple) return
          if (e.key === 'ArrowLeft') {
            e.preventDefault()
            aller(-1)
          }
          if (e.key === 'ArrowRight') {
            e.preventDefault()
            aller(1)
          }
        }}
        sx={{
          position: 'relative',
          width,
          // ⭐ Une HAUTEUR MAXIMALE, pas un format imposé. Le cadre s'adapte à
          // l'image ; c'est l'inverse qui déformait ou rognait.
          ...(height === undefined ? { maxHeight: '78vh' } : { height }),
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'action.hover',
          display: 'grid',
          placeItems: 'center',
        }}
      >
        {courante ? (
          <Box
            component="img"
            src={courante}
            alt=""
            sx={{
              // ⭐ `contain` et non `cover` : le visuel entre ENTIER dans le
              // cadre, sans être rogné ni étiré. C'est l'écran où l'on vient
              // REGARDER l'image — la vignette peut rogner, pas lui.
              display: 'block',
              maxWidth: '100%',
              maxHeight: '78vh',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
            }}
          />
        ) : (
          <InventoryIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
        )}

        {multiple && (
          <>
            <IconButton
              size="small"
              aria-label="Visuel précédent"
              onClick={() => aller(-1)}
              sx={{
                position: 'absolute',
                left: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,.45)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,.65)' },
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              size="small"
              aria-label="Visuel suivant"
              onClick={() => aller(1)}
              sx={{
                position: 'absolute',
                right: 4,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,.45)',
                color: '#fff',
                '&:hover': { bgcolor: 'rgba(0,0,0,.65)' },
              }}
            >
              <ChevronRightIcon />
            </IconButton>
          </>
        )}
      </Box>

      {multiple && (
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 2 }}>
          {images.map((src, i) => (
            <Box
              key={src.slice(-24) + i}
              component="button"
              type="button"
              aria-label={`Aller au visuel ${i + 1}`}
              aria-current={i === index}
              onClick={() => setIndex(i)}
              sx={{
                width: 8,
                height: 8,
                p: 0,
                border: 0,
                borderRadius: '50%',
                cursor: 'pointer',
                bgcolor: i === index ? 'primary.main' : 'action.disabled',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
